import { useActionData, useNavigate, useSubmit, type SubmitOptions } from "react-router";
import type {
  ActionHandlerReturnType,
  ActionPayloadError,
  ActionPayloads,
  ActionPayloadType,
} from "~/lib/actions/payloads.client";
import { useCallback, useEffect, useRef, useState } from "react";
import { logDebug, logError } from "~/lib/logger";
import { toast } from "sonner";
import { useDebouncedCallback } from "use-debounce";

/**
 * Submit to action handler
 *
 * @usage
 *
 * ```ts
 * const submit = useSubmit();
 *
 * submit(submitActionPayload("auth.login", data));
 * ```
 *
 */
export function useAction<T extends ActionPayloads["type"]>(
  type: T,
  extra?: {
    onSuccess?: (data: ActionPayloadType<T>["outputData"]) => void;
    onSuccessRedirectTo?: (data: ActionPayloadType<T>["outputData"]) => string;
    onError?: (error: ActionPayloadError) => void;
    toastOnSuccess?: {
      message: string;
    };
    toastOnError?: {
      message?: string;
    };
  }
) {
  // a ref to track if we've handled the action
  const actionIdRef = useRef<string | null>(null);
  // Track whether this instance has a pending submission
  const hasPendingSubmissionRef = useRef<boolean>(false);
  // Track current AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  // track the action return data
  const [actionDataThisActionOnly, setActionDataThisActionOnly] = useState<ActionHandlerReturnType<T> | undefined>(
    undefined
  );

  // track the state of the action
  const [actionState, setActionState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  // Native submit function
  const nativeSubmit = useSubmit();

  // Get the action data
  const actionData = useActionData<ActionHandlerReturnType>();

  // Get the navigate function
  const nativeNavigate = useNavigate();

  // Cancel previous requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Clear pending flag on unmount
      hasPendingSubmissionRef.current = false;
    };
  }, []);

  //
  // handle success
  //
  const handleSuccess = useCallback(
    (data: ActionPayloadType<T>["outputData"]) => {
      // console.log("handleSuccess", data);

      extra?.onSuccess?.(data);
      setActionState("success");

      if (extra?.toastOnSuccess) {
        toast.success(extra.toastOnSuccess.message);
      }

      if (extra?.onSuccessRedirectTo) {
        const redirectTo = extra.onSuccessRedirectTo(data);
        // console.log("redirectTo", redirectTo);

        if (!redirectTo) {
          toast.error("No redirect to provided");
          return;
        }

        // window.location.href = redirectTo;
        nativeNavigate(redirectTo);
      }
    },
    [extra, nativeNavigate]
  );

  //
  // handle error
  //
  const handleError = useCallback(
    (error: ActionPayloadError) => {
      extra?.onError?.(error);
      setActionState("error");

      // if (extra?.toastOnError) {
      toast.error(extra?.toastOnError?.message ?? error.message_safe);
      // }
    },
    [extra]
  );

  //
  // use effect to provide success and error callbacks
  //
  useEffect(() => {
    logDebug("use effect to provide success and error callbacks");

    // if we don't have an action id, return
    if (!actionData?._id) return;

    // if we've already handled the action, return
    if (actionIdRef.current === actionData._id) return;

    // If the action data is not the same as the type, this is not for us, return
    if (actionData?.currentAction !== type) return;

    // CRITICAL: Only handle this action if THIS instance initiated the submission
    // This prevents multiple useAction instances from handling the same action response
    if (!hasPendingSubmissionRef.current) {
      logDebug("useAction: Ignoring action response - not initiated by this instance");
      return;
    }

    // set the action id
    actionIdRef.current = actionData._id;

    // Clear the pending submission flag
    hasPendingSubmissionRef.current = false;

    // set the action data this action only
    // we can do this type assertion because we know the action data is for this action
    setActionDataThisActionOnly(actionData as ActionHandlerReturnType<T>);

    // If the action is successful, call the onSuccess callback
    if (actionData.success) {
      const returnData = actionData.data?.[type];
      handleSuccess(returnData as ActionPayloadType<T>["outputData"]);
      return;
    }

    if (actionData.error) {
      // If the action is not successful, call the onError callback
      handleError(actionData.error);
      return;
    }

    // This should never happen, so it's also an error
    logError("useAction: unknown action state", {
      actionData,
    });
    setActionState("error");
  }, [actionData, type, extra, handleError, handleSuccess]);

  //
  // Submit the action payload
  //
  /**
   * Submit the action payload, in a debounced manner
   */
  const submitActionPayload = useDebouncedCallback(
    (inputData: ActionPayloadType<T>["inputData"], submitOptions: SubmitOptions = {}) => {
      try {
        // Cancel any previous requests
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create a new AbortController
        abortControllerRef.current = new AbortController();

        // Set the action state to submitting
        setActionState("submitting");

        // Mark that this instance has a pending submission
        hasPendingSubmissionRef.current = true;

        // Add the signal to the options
        const options = {
          ...submitOptions,
          method: "post" as const,
          encType: "application/json" as const,
          signal: abortControllerRef.current.signal,
        };

        // Submit the action payload
        nativeSubmit(
          {
            type,
            inputData,
          },
          options
        );
      } catch (error) {
        // Ignore aborted requests
        if (error instanceof DOMException && error.name === "AbortError") {
          logDebug("Request aborted in submitActionPayload");
        } else {
          console.error("Error submitting action:", error);
          setActionState("error");
        }
      }
    },
    // 1000,
    500,
    { leading: true, trailing: false }
  );

  return {
    submit: submitActionPayload,
    state: actionState,
    actionData: actionDataThisActionOnly,
  };
}
